import { OtpRequestSchema } from '@sg/backend/src/api/auth/handleOtp'
import { SignInWithOtpRequestSchema } from '@sg/backend/src/api/auth/handleSignIn'
import { useNavigate, useSearchParams } from '@solidjs/router'
import { Component, createEffect, createSignal, DEV, Show } from 'solid-js'
import { parse } from 'valibot'
import { authAxios } from '../../axios'
import AlertBag from '../../components/AlertBag'
import Loader from '../../components/Loader'
import { floor, random } from '../../lib/core/util/math/Math'
import createDefaultWait from '../../lib/createDefaultWait'
import preventDefault from '../../lib/dom/event/preventDefault'
import isDocumentHidden from '../../rx/shared/device/isDocumentHidden'
import getSessionUserId from '../../rx/shared/session/getSessionUserId'
import { updateSessionValue } from '../../rx/shared/session/sessionValue'
import { createLoadingSignal } from '../../rx/signal/create_loading_signal'
import { createAlertBagStore, setAlertBagError, setAlertBagSuccess, setAlertBagWarning } from '../../rx/store/AlertBag'

type SignInPageSearchParams = Record<'return_to', string>

const SignInPage: Component = () => {
  const navigate = useNavigate()
  const loading = createLoadingSignal()
  const ab = createAlertBagStore()
  const [email, setEmail] = createSignal('')
  const [otp, setOtp] = createSignal('')
  const [requestToken, setRequestToken] = createSignal<string | null>(null)

  const [searchParams] = useSearchParams<SignInPageSearchParams>()

  // the user logs in using another tab,
  // then redirect them to the page they were on
  let navigating = false
  function navigateToIntended() {
    const { return_to } = searchParams
    if (navigating && return_to) {
      return
    }
    navigating = true
    // console.info('navigating')
    navigate(return_to || '/')
  }
  // createEffect(() => {
  //   console.log('getSessionUserId()', getSessionUserId())
  // })
  createEffect(() => {
    if (!isDocumentHidden() && getSessionUserId()) {
      // if the user has multiple tabs open,
      // we don't want to redirect all of them at the same time.
      createDefaultWait(floor(2 + (random() * 8))).then(navigateToIntended)
    }
  })

  const handleRequestOtp = async (e: SubmitEvent) => {
    preventDefault(e)
    setAlertBagError(ab, null)
    setAlertBagSuccess(ab, null)

    try {
      loading.start()

      const payload = parse(OtpRequestSchema, {
        email: email(),
      })
      const result = await authAxios.post('/auth/otp', payload)

      setAlertBagSuccess(ab, 'Check your email for a code!')
      setRequestToken(result.data.token) // Assume the API returns the UUID associated with the OTP request
      if (DEV) {
        const { debug_otp } = result.data
        if (result.data.debug_otp) {
          setAlertBagWarning(ab, `DEBUG OTP: ${debug_otp}`)
        }
      }
    } catch (error) {
      console.error(error)
      setAlertBagError(ab, error as Error)
    } finally {
      loading.end()
    }
  }

  const handleVerifyOtp = async (e: SubmitEvent) => {
    preventDefault(e)
    setAlertBagError(ab, null)
    setAlertBagSuccess(ab, null)

    try {
      loading.start()

      const payload = parse(SignInWithOtpRequestSchema, {
        otp: otp(),
        token: requestToken(),
      })
      const result = await authAxios.post('/auth/sign-in', payload)

      if (result.data) {
        updateSessionValue(result.data)
      }

      setAlertBagSuccess(ab, 'Login Successful!')
      await createDefaultWait(1000).then(navigateToIntended)
    } catch (error) {
      console.error(error)
      setAlertBagError(ab, error as Error)
    } finally {
      loading.end()
    }
  }

  return (
    <div class="d-flex justify-content-center">
      <div class="col-6">
        <h1>Simple Tactics</h1>
        <Show when={!requestToken()} fallback={
          <form onSubmit={handleVerifyOtp}>
            <div class="form-group mb-3">
              <label class="form-label" for="otp">OTP Code</label>
              <input
                id="otp"
                class="form-control"
                type="text"
                placeholder="Enter the OTP code"
                value={otp()}
                onChange={(e) => setOtp(e.currentTarget.value)}
              />
            </div>
            <div class="form-group mb-3">
              <button type="submit" class="btn btn-primary d-block">
                {loading() ? <span>Verifying...</span> : <span>Verify OTP</span>}
              </button>
            </div>
            <AlertBag store={ab} />
          </form>
        }>
          <form onSubmit={handleRequestOtp}>
            <div class="form-group mb-3">
              <p class="form-text">Enter your email to receive an OTP code</p>
              <label class="form-label" for="email">Email</label>
              <input
                id="email"
                class="form-control"
                type="email"
                placeholder="Your email"
                value={email()}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
            </div>
            <div class="form-group mb-3">
              <button type="submit" class="btn btn-primary d-block">
                {loading() ? <span>Loading...</span> : <span>Send OTP</span>}
              </button>
            </div>
            <AlertBag store={ab} />
          </form>
        </Show>
      </div>
      <Loader signal={loading} />
    </div>
  )
}

export default SignInPage
